import { isApiError, type ApiResponse } from '@liveforge/shared';
import { env } from '../env';
import { auth } from './firebase';

/** An API call that came back with a non-2xx status or an `{ error }` body. */
export class ApiRequestError extends Error {
  readonly status: number;
  readonly details: Record<string, string[]> | undefined;

  constructor(message: string, status: number, details?: Record<string, string[]>) {
    super(message);
    this.name = 'ApiRequestError';
    this.status = status;
    this.details = details;
  }

  /** True when the requested resource does not exist (invalid guest token, deleted Show...). */
  get isNotFound(): boolean {
    return this.status === 404;
  }
}

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE';
  body?: unknown;
  /**
   * Attach the Firebase ID token. Guest routes must not send one, so this is
   * explicit rather than inferred.
   */
  authenticated?: boolean;
  signal?: AbortSignal;
}

/**
 * The current Firebase ID token. Exported because the layout WebSocket
 * authenticates with the bare token rather than an Authorization header, and
 * duplicating this would risk the two drifting apart on refresh semantics.
 */
export async function idToken(): Promise<string> {
  const user = auth.currentUser;
  if (!user) {
    throw new ApiRequestError('You are not signed in.', 401);
  }
  // The SDK caches this and refreshes it automatically when close to expiry.
  return user.getIdToken();
}

async function authorizationHeader(): Promise<string> {
  return `Bearer ${await idToken()}`;
}

async function readErrorMessage(response: Response): Promise<ApiRequestError> {
  let message = `Request failed with status ${response.status}`;
  let details: Record<string, string[]> | undefined;
  try {
    const payload: unknown = await response.json();
    if (payload && typeof payload === 'object' && 'error' in payload) {
      const body = payload as { error: unknown; details?: Record<string, string[]> };
      if (typeof body.error === 'string' && body.error.length > 0) {
        message = body.error;
      }
      details = body.details;
    }
  } catch {
    // Body was empty or not JSON: keep the status-based message.
  }
  return new ApiRequestError(message, response.status, details);
}

async function send(path: string, options: RequestOptions): Promise<Response> {
  const { method = 'GET', body, authenticated = true, signal } = options;

  const headers: Record<string, string> = { Accept: 'application/json' };
  if (body !== undefined) {
    headers['Content-Type'] = 'application/json';
  }
  if (authenticated) {
    headers['Authorization'] = await authorizationHeader();
  }

  const init: RequestInit = { method, headers };
  if (body !== undefined) {
    init.body = JSON.stringify(body);
  }
  if (signal) {
    init.signal = signal;
  }

  let response: Response;
  try {
    response = await fetch(`${env.apiBaseUrl}${path}`, init);
  } catch (cause) {
    if (cause instanceof DOMException && cause.name === 'AbortError') {
      throw cause;
    }
    throw new ApiRequestError('Cannot reach the LiveForge server.', 0);
  }

  if (!response.ok) {
    throw await readErrorMessage(response);
  }
  return response;
}

/** Performs a request and unwraps the `{ data }` envelope. */
export async function apiRequest<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const response = await send(path, options);
  const payload = (await response.json()) as ApiResponse<T>;
  if (isApiError(payload)) {
    throw new ApiRequestError(payload.error, response.status, payload.details);
  }
  return payload.data;
}

/** Performs a request that answers `204 No Content`. */
export async function apiRequestVoid(path: string, options: RequestOptions = {}): Promise<void> {
  await send(path, options);
}

/** Turns anything thrown by `apiRequest` into a message fit for the UI. */
export function errorMessage(error: unknown): string {
  if (error instanceof ApiRequestError) {
    return error.message;
  }
  if (error instanceof Error) {
    return error.message;
  }
  return 'Something went wrong.';
}

/**
 * Origin the layout relay is served from. Socket.IO is mounted beside the REST
 * router rather than under it, so the `/api` path comes off; an empty string
 * means same-origin, which is what a deployed build gets.
 */
export function relayOrigin(): string {
  return env.apiBaseUrl.replace(/\/api\/?$/, '');
}
